// Import 3rd-party dependencies
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  SharedInsight,
  ThumbnailSet,
  Thumbnail,
  UsedInsight,
  Trending,
  SearchResponse,
  SearchRequest,
  EntityType
} from '@microsoft/microsoft-graph-types';
import moment from 'moment';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, switchMap, toArray } from 'rxjs/operators';

// Import project-specific dependencies
import { GraphApiService } from '@app/services/graph-api.service';
import { environment } from '@app/environments/environment';
import { IGraphApiResponse } from '@app/interfaces';
import { ENTERPRISE_SEARCH_SERVICE_CONSTS } from '@app/config/constants';

export interface ISearchResultItem {
  id?: number;
  icon?: string;
  icontype?: string;
  iconbgcolor?: string;
  title?: string;
  name?: string;
  url?: string;
  displayurl?: string;
  webshorturl?: string;
  preimgurl?: string;
  createddate?: string;
  modifieddate?: string;
  lastAccessed?: string;
  createdby?: string;
  type?: string;
  html?: string;
  desc?: string;
  itemtype?: string;
  hasfile?: boolean;
  email?: string;
  weight?: string;
}

export interface fileexts {
  value: string;
  viewValue: string;
}

interface IInsightFileType {
  fileExt: string;
  hasFile: boolean;
}

interface IInsightFileTypeMap {
  [fileType: string]: string;
}

export interface SearchDataContent {
  contentSource: string;
  rank: number;
  resource?: {
    [key: string]: unknown;
  };
}
export interface SearchData {
  searchTerms?: string[];
  total?: number;
  searchData: SearchDataContent[];
  moreResultsAvailable?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EnterpriseSearchService extends GraphApiService {
  consts = ENTERPRISE_SEARCH_SERVICE_CONSTS;
  private fileExtensions: IInsightFileTypeMap = this.consts.GRAPH_API_INSIGHT_RESOURCE_TYPE_FILE_EXTENSION_MAPPING;
  private API_URL = `${environment.GRAPH.URL}/${environment.GRAPH.VERSIONS.V1}`;

  insightsUsed$ = this.get<IGraphApiResponse>(`${this.API_URL}/me/insights/used`, {
    $top: this.consts.QUERY_INSIGHTS_TOP
  }).pipe(
    switchMap((result) => result.value.map((u) => u as UsedInsight)),
    toArray(),
    shareReplay()
  );

  usedFiles$ = this.get<IGraphApiResponse>(`${this.API_URL}/me/drive/recent`, {
    $select: this.consts.QUERY_SELECT_USED_FILE
  }).pipe(
    switchMap((result) => result.value.map((u) => u as UsedInsight)),
    toArray(),
    shareReplay()
  );

  trending$ = this.get<IGraphApiResponse>(`${this.API_URL}/me/insights/trending`, {
    $filter: this.consts.QUERY_TRENDING_FILTER_RESOURCEVISUALIZATION_TYPE
  }).pipe(
    switchMap((result) => result.value.map((u) => u as Trending)),
    toArray(),
    shareReplay()
  );

  trendingSharepoint$ = this.get<IGraphApiResponse>(`${this.API_URL}/me/insights/trending`, {
    $filter: this.consts.QUERY_TRENDING_SP_FILTER_RESOURCEVISUALIZATION_TYPE
  }).pipe(
    switchMap((result) => result.value.map((u) => u as Trending)),
    toArray(),
    shareReplay()
  );

  trendingSearch$ = this.trending$.pipe(
    switchMap((trending) =>
      from(trending).pipe(
        mergeMap((trend) => {
          let subs$: Observable<Thumbnail>;
          if (trend.resourceReference.type && trend.resourceReference.type.includes('driveItem')) {
            subs$ = this.getThumbnail(trend.resourceReference.id);
          } else {
            subs$ = of(null) as Observable<Thumbnail>;
          }
          return subs$.pipe(
            map((thumbnail) => {
              const fileType = this.getInsightFileType(
                trend.resourceVisualization.type,
                trend.resourceVisualization.containerType
              );
              const iconExt = fileType.fileExt;
              const hasFile = fileType.hasFile;
              const url = trend.resourceReference.hasOwnProperty('webUrl')
                ? trend.resourceReference.webUrl
                : trend.resourceVisualization.containerWebUrl;
              return {
                icon: iconExt,
                icontype:
                  trend.resourceVisualization.type.toLowerCase() === 'spsite'
                    ? 'Sharepoint Site'
                    : trend.resourceVisualization.type,
                title: trend.resourceVisualization.title,
                name: trend.resourceVisualization.title,
                url: url,
                displayurl: thumbnail && thumbnail.url,
                webshorturl: url,
                preimgurl: iconExt,
                itemtype: 'trending',
                type: trend.resourceVisualization.containerType,
                hasfile: hasFile,
                weight: trend.weight.toString()
              } as ISearchResultItem;
            })
          );
        }),
        toArray()
      )
    )
  );

  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  private getInsightsShared(email: string): Observable<SharedInsight[]> {
    return this.get<IGraphApiResponse>(`${this.API_URL}/me/insights/shared`, {
      $top: this.consts.QUERY_INSIGHTS_TOP,
      $filter: `${this.consts.QUERY_SHARED_INSIGHTS_FILTER_LASTSHARED_SHAREDBY_ADDRESS} '${email}'`
    }).pipe(
      switchMap((result) => result.value.map((u) => u as SharedInsight)),
      toArray(),
      shareReplay()
    );
  }

  getThumbnail(path: string): Observable<Thumbnail> {
    return this.get<IGraphApiResponse>(`${this.API_URL}/${path}/thumbnails`, {
      $select: this.consts.QUERY_GET_THUMBNAIL_SELECT
    }).pipe(
      switchMap((result) => result.value.map((r) => r as ThumbnailSet)),
      toArray(),
      map((thumbsArray) => (thumbsArray[0] && thumbsArray[0].medium ? thumbsArray[0].medium : null)),
      catchError(() => of(null) as Observable<Thumbnail>)
    );
  }

  getSiteIconName(val: string): string {
    if (val) {
      val = val.trim().toUpperCase();
    }

    return val && val.length > 1 ? `${val[0]}${val[val.length - 1]}` : this.consts.SITE_ICON_NAME_NA;
  }

  getInsightFileType(fileType: string, containerType: string): IInsightFileType {
    const insightFileType: IInsightFileType = {
      fileExt: '',
      hasFile: true
    };
    fileType = fileType.toLowerCase();
    const mapKeys = Object.keys(this.consts.GRAPH_API_INSIGHT_RESOURCE_TYPE_FILE_EXTENSION_MAPPING);

    if (mapKeys.includes(fileType)) {
      insightFileType.fileExt = this.fileExtensions[fileType];
    } else if (mapKeys.includes(containerType)) {
      insightFileType.fileExt = this.fileExtensions[containerType];
    } else {
      if (fileType === 'other') {
        insightFileType.fileExt = fileType;
      } else {
        insightFileType.fileExt = this.getSiteIconName(fileType);
      }
      insightFileType.hasFile = false;
    }

    return insightFileType;
  }

  getFileExt(file: string): string {
    const fileExt = file.substr(file.lastIndexOf('.'), file.length).replace('.', '');
    return fileExt.length >= this.consts.FILE_EXT_MIN_LENGTH ? fileExt : this.consts.SITE_ICON_NAME_NA;
  }

  getInsights(email: string): Observable<ISearchResultItem[]> {
    return combineLatest([this.getInsightsShared(email), this.insightsUsed$]).pipe(
      map(([sharedInsights, usedInsights]) => [...sharedInsights, ...usedInsights]),
      switchMap((insights) =>
        from(insights).pipe(
          mergeMap((insight) => {
            let subs$: Observable<Thumbnail>;
            if (insight.resourceReference.type && insight.resourceReference.type.includes('driveItem')) {
              subs$ = this.getThumbnail(insight.resourceReference.id);
            } else {
              subs$ = of(null) as Observable<Thumbnail>;
            }

            return subs$.pipe(
              map((thumbnail) => {
                const isSharedInsight = (insight: SharedInsight | UsedInsight): insight is SharedInsight =>
                  (insight as SharedInsight).lastShared != undefined;
                const shared = isSharedInsight(insight);

                const lastAccessedTime = moment(
                  shared
                    ? (<SharedInsight>insight).lastShared.sharedDateTime
                    : (<UsedInsight>insight).lastUsed.lastAccessedDateTime
                );

                if (insight.resourceVisualization.type && insight.resourceVisualization.containerType) {
                  const fileType = this.getInsightFileType(
                    insight.resourceVisualization.type,
                    insight.resourceVisualization.containerType
                  );
                  const iconExt = fileType.fileExt;
                  const hasFile = fileType.hasFile;
                  const thumbnailUrl =
                    insight.resourceReference.type.includes('driveItem') && thumbnail && thumbnail.url;
                  const resultItem: ISearchResultItem = {};

                  if (!shared) {
                    resultItem.icon = this.consts.ICON_EDIT_FILE;
                    resultItem.icontype = insight.resourceVisualization.type;
                    resultItem.title = insight.resourceVisualization.title;
                    resultItem.itemtype = this.consts.ITEM_TYPE_INSIGHTS;
                    resultItem.type = this.consts.ITEM_TYPE;
                    resultItem.modifieddate = (<UsedInsight>insight).lastUsed.lastModifiedDateTime;
                  } else {
                    resultItem.icon = this.consts.ICON_SHARED_FILE;
                    resultItem.title = (<SharedInsight>insight).lastShared.sharedBy.displayName;
                    resultItem.itemtype = this.consts.ICON_SHARED_FILE;
                    resultItem.type = this.consts.SHARED_TYPE;
                    resultItem.modifieddate = (<SharedInsight>insight).lastShared.sharedDateTime;
                  }

                  resultItem.iconbgcolor = !hasFile ? 'background-color: #c74918' : '';
                  resultItem.name = insight.resourceVisualization.title;
                  resultItem.url = insight.resourceReference.webUrl;
                  resultItem.displayurl = thumbnailUrl;
                  resultItem.webshorturl = insight.resourceReference.webUrl;
                  resultItem.preimgurl = !hasFile ? this.getFileExt(insight.resourceReference.webUrl) : iconExt;
                  resultItem.hasfile = hasFile;
                  resultItem.lastAccessed = lastAccessedTime.fromNow();
                  return resultItem;
                }
              })
            );
          })
        )
      ),
      toArray(),
      map((res) => {
        let counter = 1;
        return res.filter((item) => item !== undefined).map((r) => ({ ...r, id: counter++ } as ISearchResultItem));
      })
    );
  }

  contentSourceSearch(queryString: string, contentSources?: string[], from?: number): Observable<SearchData> {
    const betaUrl = `${environment.GRAPH.URL}/${environment.GRAPH.VERSIONS.BETA}`;

    const requestData: SearchRequest = {
      entityTypes: this.consts.CONTENT_SOURCE_SEARCH_REQUESTDATA_ENTITYTYPE as EntityType[],
      contentSources,
      query: { queryString },
      fields: this.consts.CONTENT_SOURCE_SEARCH_REQUESTDATA_FIELDS
    };

    if (from > 0) {
      requestData.from = from;
    }

    return this.post<IGraphApiResponse>(
      `${betaUrl}/search/query`,
      { requests: [requestData] },
      null,
      this.consts.CONTENT_SOURCE_SEARCH_REQUESTDATA_HEADERS
    ).pipe(
      map((res) => {
        if (res.value && res.value[0]) {
          const searchResult = res.value[0] as SearchResponse;
          const searchDataRet: SearchData = {
            moreResultsAvailable: false,
            searchData: [],
            searchTerms: []
          };

          const hitContainer = searchResult.hitsContainers[0];
          if (!hitContainer || hitContainer.total === 0) {
            return null;
          }

          searchDataRet.searchTerms = searchResult.searchTerms;
          searchDataRet.moreResultsAvailable = hitContainer.moreResultsAvailable;
          searchDataRet.total = hitContainer.total;

          hitContainer.hits.forEach((hit) => {
            const newHit = {
              rank: hit.rank,
              contentSource: hit.contentSource,
              resource: (<{ properties: unknown }>hit.resource).properties
            };
            if (newHit.resource['syscreatedby']) {
              delete newHit.resource['syscreatedby'];
            }

            searchDataRet.searchData = [...searchDataRet.searchData, newHit as SearchDataContent];
          });

          return searchDataRet;
        } else {
          return null;
        }
      })
    );
  }
}
